import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { useAuthStore, type UserProfile } from '@/features/auth/store/authStore';

export interface ProfileData {
  id: string;
  user_id: string;
  first_name?: string | null;
  last_name?: string | null;
  display_name?: string | null;
  preferred_name?: string | null;
  grade_level?: number | null;
  phone_number?: string | null;
  avatar?: string | null;
  story_preferences?: string[] | null;
  music_enabled?: boolean;
  profile_type: 'student' | 'parent';
}

export interface StudentProfile {
  id: string;
  username: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  roles: string[];
  created_at: string;
  last_login?: string | null;
  is_active: boolean;
  profile?: ProfileData | null;
}

export interface StudentProfileUpdate {
  first_name?: string | null;
  last_name?: string | null;
  preferred_name?: string | null;
  phone_number?: string | null;
  avatar?: string | null;
  story_preferences?: string[] | null;
}

/**
 * Hook to fetch the current user's complete profile (works for all user types)
 */
export function useStudentProfile() {
  return useQuery({
    queryKey: ['user', 'profile'],
    queryFn: async () => {
      const data = await apiClient.get<StudentProfile>('/auth/me');
      return data;
    },
  });
}

/**
 * Hook to update the current user's profile (works for all user types)
 */
export function useUpdateStudentProfile() {
  const queryClient = useQueryClient();
  const updateUser = useAuthStore((state) => state.updateUser);

  return useMutation({
    mutationFn: async (updates: StudentProfileUpdate) => {
      const data = await apiClient.put<StudentProfile>('/auth/me', updates);
      return data;
    },
    onSuccess: (data) => {
      // Update the query cache
      queryClient.setQueryData(['user', 'profile'], data);

      // Update auth store if needed (for display name, etc.)
      if (data.profile) {
        updateUser({
          profile: {
            ...useAuthStore.getState().user?.profile,
            displayName: data.profile.display_name,
            firstName: data.profile.first_name,
            lastName: data.profile.last_name,
          } as UserProfile,
        });
      }
    },
  });
}
